"use client";
import {
  Button,
  Field,
  Input,
  Label,
  makeStyles,
  MessageBar,
  Spinner,
  tokens,
} from "@fluentui/react-components";
import { ArrowLeftFilled, Link20Regular } from "@fluentui/react-icons";
import { getOpenLink } from "@skanska/vsa-file-icon";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { chapterImageSize } from "../../constants/ImageConstants";
import {
  useGetChapterImage,
  useGetCurrentPublishedChapterNames,
  useUpdateChapterImage,
} from "../../hooks/useImages";
import { useUnsavedChanges } from "../../hooks/useUnsavedChanges";
import { ClientRoutes } from "../../models/ClientRoutes";
import { Image } from "../../models/Image";
import {
  clearErrorsByType,
  handleFileNameChangeUnified,
  handleFileSelection,
  ImageUploadType,
  isImageFile,
  splitFileName,
  validateImageFormUnified,
} from "../../services/ImageService";
import { missingImagePlaceholder } from "../../services/MissingImagePlaceholder";
import { SkanskaColorTokens } from "../../style/colors";
import { ButtonWithLoading } from "../ButtonWithLoading";
import { ImageDeleteButton } from "./ImageDeleteButton";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingVerticalM,
    paddingRight: tokens.spacingVerticalM,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingBottom: tokens.spacingVerticalL,
  },
  title: {
    fontSize: tokens.fontSizeHero800,
    fontWeight: tokens.fontWeightSemibold,
    margin: 0,
    textAlign: "center",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
    maxWidth: "800px",
    margin: "0 auto",
    padding: tokens.spacingVerticalM,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  fileSection: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  actions: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    alignItems: "center",
    paddingTop: tokens.spacingVerticalM,
  },
  actionButtons: {
    display: "flex",
    gap: tokens.spacingHorizontalM,
    justifyContent: "center",
  },
  deleteSection: {
    display: "flex",
    justifyContent: "center",
    paddingTop: tokens.spacingVerticalS,
  },
  buttons: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    paddingTop: tokens.spacingVerticalM,
  },
  notFound: {
    textAlign: "center",
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground3,
  },
  imagePreview: {
    maxWidth: "300px",
    maxHeight: "200px",
    objectFit: "contain",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: "pointer",
  },
  imageContainer: {
    display: "flex",
    gap: tokens.spacingHorizontalM,
    alignItems: "flex-start",
  },
  errorContainer: {
    minHeight: "60px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    maxWidth: "800px",
    margin: "0 auto",
    paddingBottom: tokens.spacingVerticalL,
  },
  errorMessage: {
    wordWrap: "break-word",
    overflowWrap: "break-word",
    wordBreak: "break-word",
    hyphens: "auto",
  },
  selectedFile: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalS,
  },
  fileNameInputContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "4px",
  },
  fileExtension: {
    color: "#888",
    backgroundColor: "#f3f3f3",
    border: "1px solid #ddd",
    borderRadius: "4px",
    padding: "0 8px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    userSelect: "none",
    pointerEvents: "none",
  },
  deletefileButton: {
    backgroundColor: SkanskaColorTokens.color_tertiary_red_30,
    ":hover, :focus, :active": {
      backgroundColor: SkanskaColorTokens.color_tertiary_red,
      color: SkanskaColorTokens.color_secondary_white,
    },
  },
  filePickerButton: {
    display: "flex",
    flexDirection: "column",
    margin: "4px 0",
    "::file-selector-button": {
      display: "flex",
      flexDirection: "column",
      backgroundColor: "white",
      color: "black",
      border: "1px solid gray",
      fontFamily: "Shape Sans",
      fontWeight: "bold",
      padding: "5px 12px",
      marginBottom: "8px",
      cursor: "pointer",
      borderRadius: "4px",
      ":hover": {
        backgroundColor: "lightgray",
      },
      ":active": {
        backgroundColor: "gray",
      },
    },
  },
  imageLink: {
    textDecoration: "none",
    color: "inherit",
    display: "flex",
    alignItems: "center",
    alignContent: "center",
    gap: "4px",
    marginTop: "8px",
  },
  errorList: {
    margin: "10px",
    paddingLeft: "0",
    listStyle: "none",
  },
  fileErrorMessage: {
    marginTop: "0",
  },
  fileErrorMessageWithErrors: {
    marginTop: tokens.spacingVerticalS,
  },
  imagePreviewHidden: {
    display: "none",
  },
  cancelFileButton: {
    marginTop: tokens.spacingVerticalXS,
  },
  chaptersSection: {
    paddingTop: tokens.spacingVerticalL,
  },
  chaptersList: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    marginTop: tokens.spacingVerticalS,
  },
  chapterLink: {
    textDecoration: "none",
    color: tokens.colorBrandForeground1,
    ":hover": {
      textDecoration: "underline",
    },
  },
  noChaptersMessage: {
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
  },
});

export const ChapterImageEditor = ({ id }: { id: string }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [fileName, setFileName] = useState("");
  const [newFile, setNewFile] = useState<File | null>(null);
  const [currentImage, setCurrentImage] = useState<Image | null>(null);
  const [hasFormAction, setHasFormAction] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState<boolean>(true);

  const { data: chapterImage, isLoading: chapterImageLoading } =
    useGetChapterImage(id);
  const { data: publishedChapterNames, isLoading: chapterNamesLoading } =
    useGetCurrentPublishedChapterNames();
  const { mutateAsync: updateChapterImage, isPending: isUpdatingChapter } =
    useUpdateChapterImage(id);

  const currentImageData = chapterImage;
  const isLoading = chapterImageLoading || chapterNamesLoading;
  const publishedNames = publishedChapterNames;

  // Set image data when loaded
  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect */
    if (currentImageData) {
      setCurrentImage(currentImageData);
      setTitle(currentImageData.title);
      setFileName(currentImageData.fileName || currentImageData.title);
      setHasFormAction(false);

      // If image is base64, set loading to false immediately
      if (currentImageData.webUrl?.startsWith("data:")) {
        setImageLoading(false);
      } else {
        setImageLoading(true);
      }
    }
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [currentImageData]);

  useEffect(() => {
    if (publishedNames && currentImage) {
      const validateForm = async () => {
        const formData = {
          title: title.trim(),
          name: fileName.trim(),
          file: newFile,
        };
        const validation = await validateImageFormUnified(
          formData,
          "chapter",
          publishedNames,
          chapterImageSize.width,
          chapterImageSize.height,
          t,
          currentImage.fileName || currentImage.title
        );
        setErrors(validation.errors);
      };
      validateForm();
    }
  }, [title, fileName, publishedNames, currentImage, newFile, t]);

  useUnsavedChanges(hasFormAction, t("unsavedChangesMessage"));

  const handleFileSelect = async (selectedFile: File) => {
    setHasFormAction(true);
    const result = await handleFileSelection(
      selectedFile,
      "chapter",
      chapterImageSize.width,
      chapterImageSize.height,
      errors,
      t
    );

    setErrors(result.updatedErrors);

    if (result.isValid) {
      setNewFile(selectedFile);
      setFileError(null);

      // Extract the new extension from the selected file
      const selectedFileExt = selectedFile.name.includes(".")
        ? selectedFile.name.substring(selectedFile.name.lastIndexOf("."))
        : "";

      // Keep the current name but replace with the new extension
      const currentName = splitFileName(fileName).name;
      const newFileName = currentName + selectedFileExt;

      setFileName(newFileName);

      // Validate the new filename
      const fileNameResult = handleFileNameChangeUnified(
        newFileName,
        result.updatedErrors,
        publishedNames,
        "chapter",
        t,
        currentImage?.fileName || currentImage?.title
      );
      setErrors(fileNameResult.updatedErrors);
    } else {
      setNewFile(null);
      setFileError(result.fileError || null);
    }
  };

  const handleCancelFileSelection = () => {
    setHasFormAction(true);
    setNewFile(null);
    setFileError(null);
    setErrors((prev) => clearErrorsByType(prev, "file", t));
  };

  const handleSave = async () => {
    if (!currentImage || !id) return;

    setErrors([]);
    const formData = {
      title: title.trim(),
      name: fileName.trim(),
      file: newFile,
    };
    const validation = await validateImageFormUnified(
      formData,
      "chapter",
      publishedNames,
      chapterImageSize.width,
      chapterImageSize.height,
      t,
      currentImage.fileName || currentImage.title
    );

    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append(
        "VersionId",
        currentImage.versionId || currentImage.id
      );
      formDataToSend.append("Title", title.trim());
      formDataToSend.append("FileName", fileName.trim());

      if (newFile) {
        formDataToSend.append("NewImageFile", newFile);
      }

      const updatedImage = await updateChapterImage(formDataToSend);

      // Update currentImage state with the response
      if (updatedImage) {
        setCurrentImage(updatedImage);
      }

      setNewFile(null);
      setHasFormAction(false);
    } catch (error) {
      console.error("Failed to save image:", error);
      setErrors([t("saveImageError") || "Failed to save image"]);
    }
  };

  if (isLoading) {
    return <Spinner size="large" />;
  }

  if (!currentImage) {
    return (
      <div className={classes.root}>
        <div className={classes.notFound}>{t("imageNotFound")}</div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <h1 className={classes.title}>{t("editChapterImage")}</h1>
      </div>

      <div className={classes.errorContainer}>
        {errors.length > 0 && (
          <MessageBar intent="error">
            <ul className={classes.errorList}>
              {errors.map((error, index) => (
                <li key={index} className={classes.errorMessage}>
                  {error}
                </li>
              ))}
            </ul>
          </MessageBar>
        )}
        {fileError && (
          <MessageBar
            intent="error"
            className={
              errors.length > 0
                ? classes.fileErrorMessageWithErrors
                : classes.fileErrorMessage
            }
          >
            <div className={classes.errorMessage}>{fileError}</div>
          </MessageBar>
        )}
      </div>

      <div className={classes.form}>
        <div className={classes.field}>
          <Label>{t("currentFile")}</Label>
          <div className={classes.imageContainer}>
            {currentImage &&
              currentImage.webUrl &&
              (isImageFile(currentImage.webUrl, currentImage.fileName) ? (
                <div>
                  {imageLoading && <Spinner size="medium" />}
                  <img
                    src={currentImage.webUrl}
                    alt={currentImage.title}
                    width={240}
                    height={180}
                    title={currentImage.title}
                    className={`${classes.imagePreview} ${imageLoading ? classes.imagePreviewHidden : ""}`}
                    onClick={() => window.open(currentImage.webUrl, "_blank")}
                    onLoad={() => setImageLoading(false)}
                    onError={(e) => {
                      setImageLoading(false);
                      if (e.currentTarget.src !== missingImagePlaceholder) {
                        e.currentTarget.src = missingImagePlaceholder;
                      }
                    }}
                  />
                </div>
              ) : (
                <>
                  <a
                    className={classes.imageLink}
                    href={getOpenLink({
                      webUrl: currentImage.webUrl,
                      fileType: currentImage.fileType,
                    })}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Link20Regular />
                    {currentImage.webUrl}
                  </a>
                </>
              ))}
          </div>
        </div>

        <div className={classes.fileSection}>
          <Field label={t("changeImage")}>
            <input
              type="file"
              accept={ImageUploadType.Chapter}
              onChange={async (e) => {
                const selectedFile = e.target.files?.[0];
                if (selectedFile) {
                  await handleFileSelect(selectedFile);
                } else {
                  handleCancelFileSelection();
                }
              }}
              className={classes.filePickerButton}
            />
          </Field>
          {newFile && (
            <div className={classes.selectedFile}>
              <Label>
                {t("selectedFile")}: <strong>{newFile.name}</strong>
              </Label>
              <div className={classes.cancelFileButton}>
                <Button
                  appearance="secondary"
                  onClick={handleCancelFileSelection}
                  size="medium"
                  className={classes.deletefileButton}
                >
                  {t("cancelSelectedNewImageFile")}
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className={classes.field}>
          <Field label={t("title")} required hint={t("imageTitleHint")}>
            <Input
              value={title}
              onChange={(_e, data) => {
                setHasFormAction(true);
                setTitle(data.value);

                // Clear existing title errors first
                let updatedErrors = clearErrorsByType(errors, "title", t);

                // Add title required error if empty
                if (!data.value.trim()) {
                  updatedErrors = [...updatedErrors, t("titleRequired")];
                }

                setErrors(updatedErrors);
              }}
              placeholder={t("enterImageTitle")}
            />
          </Field>
        </div>

        <div className={classes.field}>
          <Field label={t("fileName")} required hint={t("fileNameHint")}>
            <div className={classes.fileNameInputContainer}>
              <Input
                value={splitFileName(fileName).name}
                onChange={(_e, data) => {
                  setHasFormAction(true);
                  // Combine the edited name with the existing extension
                  const { ext } = splitFileName(fileName);
                  const newFileName = data.value + ext;
                  setFileName(newFileName);

                  const result = handleFileNameChangeUnified(
                    newFileName,
                    errors,
                    publishedNames,
                    "chapter",
                    t,
                    currentImage?.fileName || currentImage?.title
                  );

                  setErrors(result.updatedErrors);
                }}
                placeholder={t("enterFileName")}
                style={{ flex: 1 }}
              />
              <span
                className={classes.fileExtension}
                aria-disabled="true"
                tabIndex={-1}
              >
                {splitFileName(fileName).ext}
              </span>
            </div>
          </Field>
        </div>

        <div className={classes.chaptersSection}>
          <h4>{t("chapter")}</h4>

          {currentImage?.chapters && currentImage.chapters.length > 0 ? (
            <div className={classes.chaptersList}>
              {currentImage.chapters.map((chapter) => (
                <Link
                  key={chapter.id}
                  to={`/chapters/${chapter.id}`}
                  className={classes.chapterLink}
                >
                  {chapter.title}
                </Link>
              ))}
            </div>
          ) : (
            <div className={classes.noChaptersMessage}>
              <span>{t("notUsedInAnyChapter")}</span>
            </div>
          )}
        </div>

        <div className={classes.actions}>
          <div className={classes.actionButtons}>
            <ButtonWithLoading
              size="large"
              text={isUpdatingChapter ? t("saving") : t("save")}
              onClick={handleSave}
              disabled={
                !hasFormAction ||
                errors.length > 0 ||
                fileError !== null ||
                isUpdatingChapter
              }
              isLoading={isUpdatingChapter}
            />
          </div>

          <div className={classes.deleteSection}>
            <ImageDeleteButton image={currentImage} />
          </div>
        </div>
      </div>

      <div className={classes.buttons}>
        <Button
          icon={<ArrowLeftFilled />}
          size="small"
          onClick={() => navigate(ClientRoutes.MediaChapterPage)}
        >
          {t("back")}
        </Button>
      </div>
    </div>
  );
};
